import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaBars,
  FaTimes,
  FaFileAlt,
  FaSignOutAlt,
  FaShieldAlt,
  FaSignInAlt,
} from 'react-icons/fa';
import emojiDictionary from 'utils/emoji';
import './SideNavBar.css';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DarkModeToggle from 'components/Settings/DarkModeToggle';

const SideNavBar = ({ authState }) => {
  const [navState, setNavState] = useState('icons'); // 'icons', 'full'
  const { i18n, t } = useTranslation('common');

  const toggleNavBar = () => {
    if (navState === 'icons') {
      setNavState('full');
    } else {
      setNavState('icons');
    }
  };

  if (!authState || !authState.groups) {
    return null;
  }

  const adminLinks = [
    {
      to: '/admin-home',
      text: t('nav.mandates', 'Mandates'),
      icon: emojiDictionary.mandate,
    },
    {
      to: '/buildings-list',
      text: t('nav.buildings', 'Buildings'),
      icon: emojiDictionary.building,
    },
    {
      to: '/users-list',
      text: t('nav.users', 'Users'),
      icon: emojiDictionary.customer,
    },
    {
      to: '/quotes-list',
      text: t('nav.quotes', 'Quotes'),
      icon: emojiDictionary.quotes,
    },
    {
      to: '/prospector',
      text: t('nav.prospector', 'Prospector'),
      icon: emojiDictionary.prospector,
    },
    {
      to: '/products-list',
      text: t('nav.products', 'Products'),
      icon: emojiDictionary.products,
    },
    {
      to: '/settings',
      text: t('nav.settings', 'Settings'),
      icon: emojiDictionary.settings,
    },
  ];

  const switchTo = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const getFlagIcon = (lang) => {
    switch (lang) {
      case 'fr':
        return '/icons/flags/fr.webp';
      case 'it':
        return '/icons/flags/it.png';
      default:
        return '/icons/flags/en.png';
    }
  };

  const renderNavLink = (link, isIconsOnly) => {
    if (isIconsOnly) {
      return (
        <Link key={link.to} to={link.to} title={link.text}>
          <span className="emoji-icon">{link.icon}</span>
        </Link>
      );
    }

    return (
      <Link key={link.to} to={link.to}>
        <span className="nav-icon">{link.icon}</span>
        <span className="nav-text">{link.text}</span>
      </Link>
    );
  };

  const renderLangSwitch = (isIconsOnly) => (
    <div className="nav-lang-switch">
      {/* Dropdown that appears on hover */}
      <ul className="lang-dropdown">
        {['fr', 'en', 'it'].map(
          (lang) =>
            lang !== i18n.language && (
              <li key={lang}>
                <button onClick={() => switchTo(lang)}>
                  <img
                    src={getFlagIcon(lang)}
                    alt={lang}
                    style={{ width: '1.2em' }}
                  />
                  {!isIconsOnly && (
                    <span className="lang-text">
                      {lang === 'fr'
                        ? t('lang.fr', 'Français')
                        : lang === 'en'
                          ? t('lang.en', 'English')
                          : t('lang.it', 'Italiano')}
                    </span>
                  )}
                </button>
              </li>
            ),
        )}
      </ul>
      <button className="lang-button clean">
        <img
          src={getFlagIcon(i18n.language)}
          alt={i18n.language}
          style={{ width: '1.2em' }}
        />
      </button>
    </div>
  );

  const renderCollaboratorLinks = () => {
    const isIconsOnly = navState === 'icons';

    return (
      <>
        <div className="nav-main-links">
          <Link
            to="/admin-home"
            title={isIconsOnly ? t('nav.home', 'Home') : undefined}
          >
            <img
              alt="logo i-neea"
              src="/logo_square.png"
              style={{ height: isIconsOnly ? 20 : '3em' }}
            />
          </Link>
          {collaboratorLinks.map((link) => renderNavLink(link, isIconsOnly))}
        </div>
        <div className="nav-bottom-links">
          {renderLangSwitch(isIconsOnly)}
          <DarkModeToggle />

          {isIconsOnly ? (
            <Link
              to="/logout"
              title={t('nav.logOut', 'Log Out')}
              data-cy="logout-button"
            >
              <FaSignOutAlt size={20} />
            </Link>
          ) : (
            <Link to="/logout" data-cy="logout-button">
              <span className="nav-icon">
                <FaSignOutAlt />
              </span>
              <span className="nav-text">{t('nav.logOut', 'Log Out')}</span>
            </Link>
          )}
        </div>
      </>
    );
  };

  const renderAdminLinks = () => {
    const isIconsOnly = navState === 'icons';

    return (
      <>
        <div className="nav-main-links">
          <Link
            to="/admin-home"
            title={isIconsOnly ? t('nav.home', 'Home') : undefined}
          >
            <img
              alt="logo i-neea"
              src="/logo_square.png"
              style={{ height: isIconsOnly ? 20 : '3em' }}
            />
          </Link>
          {adminLinks.map((link) => renderNavLink(link, isIconsOnly))}
        </div>
        <div className="nav-bottom-links">
          {renderLangSwitch(isIconsOnly)}
          <DarkModeToggle />

          {isIconsOnly ? (
            <Link
              to="/logout"
              title={t('nav.logOut', 'Log Out')}
              data-cy="logout-button"
            >
              <FaSignOutAlt size={20} />
            </Link>
          ) : (
            <Link to="/logout" data-cy="logout-button">
              <span className="nav-icon">
                <FaSignOutAlt />
              </span>
              <span className="nav-text">{t('nav.logOut', 'Log Out')}</span>
            </Link>
          )}
        </div>
      </>
    );
  };

  const customerLinks = [
    { to: '/', text: t('nav.myMandates', 'My mandates'), icon: <FaFileAlt /> },
    {
      to: '/data-protection',
      text: t('nav.dataProtection', 'Data Protection'),
      icon: <FaShieldAlt />,
    },
  ];

  const renderCustomerLinks = () => {
    const isIconsOnly = navState === 'icons';

    return (
      <>
        <div className="nav-main-links">
          {customerLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              title={isIconsOnly ? link.text : undefined}
            >
              {isIconsOnly ? (
                React.cloneElement(link.icon, { size: 20 })
              ) : (
                <>
                  <span className="nav-icon">{link.icon}</span>
                  <span className="nav-text">{link.text}</span>
                </>
              )}
            </Link>
          ))}
        </div>
        <div className="nav-bottom-links">
          {renderLangSwitch(isIconsOnly)}
          <DarkModeToggle />

          <Link to="/logout" title={isIconsOnly ? t('nav.logOut') : undefined}>
            {isIconsOnly ? (
              <FaSignOutAlt size={20} />
            ) : (
              <>
                <span className="nav-icon">
                  <FaSignOutAlt />
                </span>
                <span className="nav-text">{t('nav.logOut', 'Log Out')}</span>
              </>
            )}
          </Link>
        </div>
      </>
    );
  };

  const collaboratorLinks = adminLinks.filter((l) =>
    ['/admin-home', '/buildings-list', '/users-list', '/quotes-list'].includes(
      l.to,
    ),
  );

  const mobileLinks = authState.isLoggedIn
    ? authState.groups.includes('admin') ||
      authState.groups.includes('employee')
      ? authState.groups.includes('admin')
        ? adminLinks
        : collaboratorLinks
      : customerLinks
    : [];

  return (
    <>
      <div className="drawer-icon" onClick={toggleNavBar}>
        {navState === 'full' ? <FaTimes size={24} /> : <FaBars size={24} />}
      </div>

      <div className={`side-nav ${navState}`}>
        <nav className="nav-links">
          {authState &&
          authState.isLoggedIn &&
          authState.groups.includes('admin') ? (
            renderAdminLinks()
          ) : authState &&
            authState.isLoggedIn &&
            authState.groups.includes('employee') ? (
            renderCollaboratorLinks()
          ) : authState &&
            authState.isLoggedIn &&
            (authState.groups.includes('customer') ||
              authState.groups.includes('partner')) ? (
            renderCustomerLinks()
          ) : authState.isLoggedIn ? (
            <>
              <div className="nav-main-links" />
              <div className="nav-bottom-links">
                {renderLangSwitch(navState === 'icons')}
                {navState === 'icons' ? (
                  <Link to="/logout" title={t('nav.logOut', 'Log Out')}>
                    <FaSignOutAlt size={20} />
                  </Link>
                ) : (
                  <Link to="/logout">
                    <span className="nav-icon">
                      <FaSignOutAlt />
                    </span>
                    <span className="nav-text">
                      {t('nav.logOut', 'Log Out')}
                    </span>
                  </Link>
                )}
              </div>
            </>
          ) : navState === 'icons' ? (
            <>
              <div className="nav-main-links" />
              <div className="nav-bottom-links">
                {renderLangSwitch(true)}
                <Link to="/signin" title={t('nav.signIn', 'Sign In')}>
                  <FaSignInAlt size={20} />
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="nav-main-links" />
              <div className="nav-bottom-links">
                {renderLangSwitch(false)}
                <Link to="/signin">
                  <span className="nav-icon">
                    <FaSignInAlt />
                  </span>
                  <span className="nav-text">{t('nav.signIn', 'Sign In')}</span>
                </Link>
              </div>
            </>
          )}
        </nav>
      </div>

      {/* Mobile bottom tab bar */}
      <div className="mobile-bottom-bar" role="navigation">
        {mobileLinks.map((link) => (
          <Link key={link.to} to={link.to} title={link.text}>
            <span>{link.icon}</span>
          </Link>
        ))}
        {authState.isLoggedIn ? (
          <Link to="/logout" title={t('nav.logOut', 'Log Out')}>
            <FaSignOutAlt size={20} />
          </Link>
        ) : (
          <Link to="/signin" title={t('nav.signIn', 'Sign In')}>
            <FaSignInAlt size={20} />
          </Link>
        )}
      </div>
    </>
  );
};
SideNavBar.propTypes = {
  authState: PropTypes.shape({
    isLoggedIn: PropTypes.bool,
    groups: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default SideNavBar;
